import React from 'react';
import { generateBreadcrumbs } from '../../breadCrumbs';
import { useLexicon } from '../../hooks/useLexicon';
import QuestionTooltip from '../../components2/QuestionTooltip';
import questionMark from '../../static/img/24questionbordered.svg';
import arrowRightMini from '../../static/img/arrowRightMini.svg';
import arrowLeftGray from '../../static/img/arrowleftgray.svg';
import Loader from '../Loader';
import { useHistory } from 'react-router-dom';
import {
  useAssignmentById,
  useActiveClassData,
  useCommunityTasks,
  useGiveFeedbackCompletedTasks,
  useMarkingCriterias,
  useSubmissionById,
  useCommentBanks,
  useSuccessCriteria,
  useShortAnswers,
} from '../state/hooks';
import { ArrowRightImg, TitleConatiner, TitleMain } from './HeaderStyle';
import { skipsQuestionIndex } from '../SubmissionView/submissionResponseUtils';

const renderBreadcrumbs = (navigationItems, history, showBackArrow, backLink, useHistoryBack) => (
  <TitleConatiner>
    {showBackArrow && (
      <TitleMain
        as="button"
        onClick={(e) => {
          e.preventDefault();
          // goBack() only when there IS somewhere to go back to. On a direct link or a
          // fresh tab the session history is one entry deep, and goBack() would either do
          // nothing (arrow reads as broken) or walk the user out of Jeddle entirely.
          if (useHistoryBack && history.length > 1) {
            history.goBack();
          } else {
            history.push((backLink || '#/tasks').replace('#', ''));
          }
        }}
        style={{
          cursor: 'pointer',
          marginRight: '8px',
          display: 'flex',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          padding: 0
        }}
      >
        <ArrowRightImg src={arrowLeftGray} alt="Back" style={{ width: '20px', height: '20px' }} />
      </TitleMain>
    )}
    {navigationItems.map((item, index) => (
      <React.Fragment key={index}>
        {isNotFirstBreadcrumb(index) && arrow()}
        {hasLink(item) ? linkedBreadcrumb(index, navigationItems, item) : nonLinkBreadcrumb(index, navigationItems, item, history)}
        {isLastBreadcrumb(index, navigationItems) && tooltip(item)}
      </React.Fragment>
    ))}
  </TitleConatiner>
);

function tooltip(item) {
  return <QuestionTooltip img={questionMark} text={item.tooltip || ''} />;
}

function nonLinkBreadcrumb(index, navigationItems, item, history) {
  return <TitleMain
    $darkBackground={index === navigationItems.length - 1}
    to={item.link}
    onClick={(e) => {
      e.preventDefault();
      if (item.link && item.link.startsWith('#')) {
        const path = item.link.substring(1);
        history.push(path);
      } else if (item.link) {
        history.push(item.link);
      }
    } }
  >
    {item.name}
  </TitleMain>;
}

function linkedBreadcrumb(index, navigationItems, item) {
  return <TitleMain
    as="span"
    $darkBackground={index === navigationItems.length - 1}
  >
    {item.name}
  </TitleMain>;
}

function arrow() {
  return <ArrowRightImg src={arrowRightMini} />;
}

function isLastBreadcrumb(index, navigationItems) {
  return index === navigationItems.length - 1;
}

function hasLink(item) {
  return !item.link;
}

function isNotFirstBreadcrumb(index) {
  return index > 0;
}

/**
 * Dispatcher. The checklist (OnboardingComplete) is a fixed full-screen
 * overlay: the route-derived crumbs below would read the URL of whatever page
 * sits UNDERNEATH it ("Tasks > Current Tasks", "My Drafts", ...), so it passes
 * `staticBreadcrumbs` instead.
 *
 * NOTE: the legacy `breadcrumbs` prop is still passed by CreateNewTask
 * (index.jsx), SubmissionResponseWrapper and FeedbacksRoot as STRING arrays
 * and has always been ignored (this function took no props) — it must STAY
 * ignored; do not dispatch on it.
 */
function HeaderTitle({ staticBreadcrumbs }) {
  if (staticBreadcrumbs?.length) {
    return <StaticHeaderTitle breadcrumbs={staticBreadcrumbs} />;
  }
  return <RouteHeaderTitle />;
}

/**
 * Prop-driven crumbs render as inert text: no link, no onClick navigation,
 * no back arrow, and no trailing QuestionTooltip (renderBreadcrumbs appends
 * one to the last crumb unconditionally — a stray empty "?" here). Mirrors
 * the mirror header's breadcrumbs-prop semantics
 * (components-onboarding/Header2/headerTitle.js).
 */
function StaticHeaderTitle({ breadcrumbs }) {
  const { t } = useLexicon();
  return (
    <TitleConatiner>
      {breadcrumbs.map((item, index) => (
        <React.Fragment key={index}>
          {isNotFirstBreadcrumb(index) && arrow()}
          <TitleMain as="span" $darkBackground={isLastBreadcrumb(index, breadcrumbs)}>
            {t(item.name)}
          </TitleMain>
        </React.Fragment>
      ))}
    </TitleConatiner>
  );
}

function RouteHeaderTitle() {
  const history = useHistory();
  const { t, lt } = useLexicon();
  const currentUrl = new URL(window.location.href, window.location.origin);

  const currentPathname =
    currentUrl.hash && currentUrl.hash !== '#'
      ? currentUrl.hash.substring(1)
      : currentUrl.pathname;

  const viewSubmissionMatch = currentPathname.match(
    /\/view\/submissions\/([^\/]+)/
  );
  const submissionMatch = currentPathname.match(/\/submissions\/([^\/]+)/);
  const documentsMatch = currentPathname.match(/\/documents\/([^\/]+)/);
  const feedbackRequestMatch = currentPathname.match(/\/feedbackRequest\/documentsReview\/([^\/]+)/i);
  const feedbackHistoryMatch = currentPathname.match(/\/feedbackHistory\/documentsReview\/([^\/]+)/i);

  const taskMatch = currentPathname.match(/\/tasks\/([^\/]+)/);
  const rubricsMatch = currentPathname.match(
    /\/markingTemplates\/rubrics\/([^\/]+)/
  );
  const successCriteriaMatch = currentPathname.match(
    /\/tools\/successcriterias\/([^\/]+)/
  );
  const shortAnswerMatch = currentPathname.match(
    /\/tools\/shortanswers\/([^\/]+)/
  );

  const viewSubmissionId = viewSubmissionMatch?.[1] || null;
  const submissionId = submissionMatch?.[1] || null;
  const documentId = documentsMatch?.[1] || null;
  const documentsReviewId = feedbackRequestMatch?.[1] || feedbackHistoryMatch?.[1] || null;
  const taskId = taskMatch?.[1] || null;
  const rubricsId = rubricsMatch?.[1] || null;
  const successCriteriaId = successCriteriaMatch?.[1] || null;
  const shortAnswerId = shortAnswerMatch?.[1] || null;

  const { data: viewableSubmission, isLoadingdata: isLoadingViewSubmission } =
    useSubmissionById(viewSubmissionId);

  const { data: currentSubmission, isLoadingdata: isLoadingSubmission } =
    useSubmissionById(submissionId);

  const { data: documentSubmission, isLoadingdata: isLoadingDocument } =
    useSubmissionById(documentId);

  const { data: documentsReviewSubmission, isLoadingdata: isLoadingDocumentsReview } =
    useSubmissionById(documentsReviewId);

  const { data: assignment, isLoadingdata: isLoadingTask } =
    useAssignmentById(taskId);

  const { data: classInfo, isLoadingdata: isLoadingClassData } = useActiveClassData();
  const { data: markingCriteria } = useMarkingCriterias();
  const {
    data: completedFeedbackTasks,
    isLoadingdata: isLoadingCompletedTasks,
  } = useGiveFeedbackCompletedTasks(null, Boolean(documentsReviewId));
  const { data: communityTasks, isLoadingdata: isLoadingCommunityTasks } =
    useCommunityTasks(null, Boolean(documentsReviewId));
  const { data: commentBanksData } = useCommentBanks();
  const { data: successCriteriaData } = useSuccessCriteria();
  const { data: shortAnswersData } = useShortAnswers();

  if (isLoadingClassData || 
    isLoadingViewSubmission ||
    isLoadingSubmission ||
    isLoadingDocument ||
    isLoadingDocumentsReview ||
    isLoadingTask ||
    (documentsReviewId && (isLoadingCompletedTasks || isLoadingCommunityTasks))
  ) {
    return <Loader />;
  }
  
  const breadCrumbs = generateBreadcrumbs({
    pathname: currentPathname,
    classInfo,
    hash: currentUrl.hash,
    submissionData: currentSubmission || documentSubmission || documentsReviewSubmission,
    assignment,
    markingCriteria,
    rubricsId,
    completedFeedbackTasks,
    communityTasks,
    viewableSubmission,
    commentBanksData,
    successCriteriaData,
    successCriteriaId,
    shortAnswersData,
  })
  // Lexicon at the render chokepoint: static crumb names like "Marking Tools"
  // map for US; entity titles pass through t() unchanged (exact-key dictionary).
  // Static crumb names/tooltips localize; entity-authored tooltips
  // (assignment titles — entityTooltip) must never be rewritten.
  const localizedBreadcrumbs = breadCrumbs.map((item) => ({
    ...item,
    name: t(item.name),
    tooltip: item.entityTooltip ? item.tooltip : lt(item.tooltip),
  }));

  // Determine if we should show back arrow and where it should go
  const isReviewPage = currentPathname.includes('/review') || currentPathname.includes('?question=');
  const isQuestionsPage = submissionId && !isReviewPage;

  let showBackArrow = false;
  let backLink = null;
  let useHistoryBack = false;

  if (isReviewPage && submissionId) {
    // On review page: back arrow goes to questions page.
    //
    // Except for a one-question task, which has no questions page — SubmissionViewWrapper
    // redirects straight past it. Pushing there would land on a screen that immediately
    // replaces itself back to here, so the arrow would read as broken. Fall back to browser
    // history, which is where the user actually came from (their task list).
    showBackArrow = true;
    useHistoryBack = skipsQuestionIndex(currentSubmission);
    backLink = useHistoryBack ? null : `#/submissions/${submissionId}`;
  } else if (isQuestionsPage) {
    // On questions page: back arrow uses browser history (goes to previous page)
    showBackArrow = true;
    useHistoryBack = true;
  }

  return renderBreadcrumbs(localizedBreadcrumbs, history, showBackArrow, backLink, useHistoryBack);
}


export default HeaderTitle;
